import { createBrowserRouter, Navigate, useParams } from "react-router-dom";
import { Main } from "../../components/layout";
import Home from "../../pages/Home/Home";
import About from "../../pages/About/About";
import Contact from "../../pages/Contact/Contact";
import Projects from "../../pages/Projects/Projects";
import ProjectDetails from "../../pages/Projects/ProjectDetails";
import Courses from "../../pages/Courses/Courses";
import CourseDetails from "../../pages/Courses/CourseDetails";
import Reviews from "../../pages/Reviews/Reviews";
import Admin from "../../pages/Admin/Admin";
import Privacy from "../../pages/Privacy/Privacy";
import Terms from "../../pages/Terms/Terms";
import NotFound from "../../pages/NotFound/NotFound";
import Login from "../../pages/Auth/Login";
import Register from "../../pages/Auth/Register";
import TeacherDashboard from "../../pages/Dashboard/TeacherDashboard";
import TeacherStudents from "../../pages/Dashboard/TeacherStudents";
import StudentDashboard from "../../pages/Dashboard/StudentDashboard";
import Academy from "../../pages/Academy/Academy";
import Tools from "../../pages/Tools/Tools";
import Collab from "../../pages/Collab/Collab";
import CreativeSamuraiLanding from "../../pages/Courses/CreativeSamuraiLanding";
import PublicOnlyRoute from "../../components/common/PublicOnlyRoute";
import Activate from "../../pages/Auth/Activate";
import { lazy, Suspense } from "react";

// The course player and studio are only for signed-in students/teachers: load them on demand.
const CoursePlayer = lazy(() => import("../../pages/Learn/CoursePlayer"));
const CourseStudio = lazy(() => import("../../pages/Learn/CourseStudio"));
const StudioIndex = lazy(() => import("../../pages/Learn/StudioIndex"));
const ChangePassword = lazy(() => import("../../pages/Auth/ChangePassword"));
const learnFallback = <div style={{ minHeight: "100vh", background: "#080808" }} />;
const adminFallback = <div style={{ minHeight: "100vh", background: "#F4F6F9" }} />;

/** Legacy /learn/:slug/studio links now live under /admin/studio/:slug. */
const LegacyStudioRedirect = () => {
    const { slug } = useParams();
    return <Navigate to={`/admin/studio/${slug}`} replace />;
};

const router = createBrowserRouter([
    {
        path: "/courses/creative-samurai",
        element: <CreativeSamuraiLanding />,
    },
    {
        path: "/studio",
        element: <Navigate to="/admin/studio" replace />,
    },
    {
        path: "/learn/:slug/studio",
        element: <LegacyStudioRedirect />,
    },
    {
        path: "/learn/:slug/:lessonId?",
        element: <Suspense fallback={learnFallback}><CoursePlayer /></Suspense>,
    },
    {
        path: "/",
        element: <Main />,
        children: [
            {
                path: "/",
                element: <Home />,
            },
            {
                path: "/projects",
                element: <Projects />,
            },
            {
                path: "/projects/:slug",
                element: <ProjectDetails />,
            },
            {
                path: "/contact",
                element: <Contact />,
            },
            {
                path: "/about",
                element: <About />,
            },
            {
                path: "/courses",
                element: <Courses />,
            },
            {
                path: "/courses/:courseSlug",
                element: <CourseDetails />,
            },
            {
                path: "/reviews",
                element: <Reviews />,
            },
            {
                path: "/admin",
                element: <Admin />,
            },
            {
                path: "/admin/enrollments",
                element: <Admin />,
            },
            {
                path: "/admin/accounts",
                element: <Admin />,
            },
            {
                path: "/admin/courses",
                element: <Admin />,
            },
            {
                path: "/admin/studio",
                element: <Suspense fallback={adminFallback}><StudioIndex /></Suspense>,
            },
            {
                path: "/admin/studio/:slug",
                element: <Suspense fallback={adminFallback}><CourseStudio /></Suspense>,
            },
            {
                path: "/account/password",
                element: <Suspense fallback={null}><ChangePassword /></Suspense>,
            },
            {
                path: "/admin/reviews",
                element: <Admin />,
            },
            {
                path: "/admin/contacts",
                element: <Admin />,
            },
            {
                path: "/admin/messages",
                element: <Admin />,
            },
            {
                path: "/academy",
                element: <Academy />,
            },
            {
                path: "/tools",
                element: <Tools />,
            },
            {
                path: "/collab",
                element: <Collab />,
            },
            {
                path: "/login",
                element: <PublicOnlyRoute><Login /></PublicOnlyRoute>,
            },
            {
                path: "/activate",
                element: <PublicOnlyRoute><Activate /></PublicOnlyRoute>,
            },
            {
                path: "/register",
                element: <PublicOnlyRoute><Register /></PublicOnlyRoute>,
            },
            {
                path: "/dashboard/teacher",
                element: <TeacherDashboard />,
            },
            {
                path: "/dashboard/teacher/students",
                element: <TeacherStudents />,
            },
            {
                path: "/dashboard/student",
                element: <StudentDashboard />,
            },
            {
                path: "/privacy",
                element: <Privacy />,
            },
            {
                path: "/terms",
                element: <Terms />,
            },
            {
                path: "*",
                element: <NotFound />,
            },
        ],
    },
]);

export default router;
